import { DateRange } from '../types/DateRange';
import { TimeIntervalType } from '../types/TimeIntervalType';
import { CustomDate } from './CustomDate';

const ROLLING_DAYS: Partial<Record<TimeIntervalType, number>> = {
  [TimeIntervalType.TODAY]: 1,
  [TimeIntervalType.THIS_WEEK]: 7,
  [TimeIntervalType.THIS_MONTH]: 30,
};

export class TimeInterval {
  public readonly days: number;
  public readonly current: DateRange;
  public readonly previous: DateRange;

  constructor(public readonly type: TimeIntervalType, customRange?: DateRange) {
    const today = new CustomDate().date;

    if (type === TimeIntervalType.MONTH_TO_DATE) {
      const lastDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      this.days = today.getDate();
      this.current = { start: new Date(today.getFullYear(), today.getMonth(), 1), end: today };
      this.previous = {
        start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        end: new Date(
          lastDayOfPreviousMonth.getFullYear(),
          lastDayOfPreviousMonth.getMonth(),
          Math.min(today.getDate(), lastDayOfPreviousMonth.getDate()),
        ),
      };
      return;
    }

    const current = this.rollingOrCustomRange(type, today, customRange);
    this.days = Math.round((current.end.getTime() - current.start.getTime()) / 86400000) + 1;
    this.current = current;
    this.previous = {
      start: new CustomDate(current.start).subtractDays(this.days).date,
      end: new CustomDate(current.start).subtractDays(1).date,
    };
  }

  private rollingOrCustomRange(type: TimeIntervalType, today: Date, customRange?: DateRange): DateRange {
    if (type === TimeIntervalType.CUSTOM) {
      if (!customRange) throw new Error('A custom time interval needs a date range');
      return { start: new CustomDate(customRange.start).date, end: new CustomDate(customRange.end).date };
    }
    const days = ROLLING_DAYS[type];
    if (!days) throw new Error('Invalid time interval type');
    return { start: new CustomDate().subtractDays(days - 1).date, end: today };
  }

  public isCurrent(date: Date): boolean {
    return date >= this.current.start && date <= this.current.end;
  }

  public isPrevious(date: Date): boolean {
    return date >= this.previous.start && date <= this.previous.end;
  }
}
